import React, { useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import noop from 'lodash/noop';
import xor from 'lodash/xor';
import memoize from '@/Framework/dataHelpers/memoize';
import DealRow from './DealRow/DealRow';
import PanelGroup from '@dealroadshow/uikit/core/components/PanelGroup';
import { CollapseAllButton, ExpandAllButton } from './DealIButtons';
import SubscribeButton from './SubscribeButtonContainer';
import LoadMoreButton from '@/finsight/ui/components/deals/DealsList/LoadMoreButton';
import { ProfileTypes } from '@/finsight/application/actions/profile/constants';
import DealContextProvider, { useDealContext } from '@/finsight/application/Deal/DealContext';
import { useParentPageContext } from '@/finsight/application/ParentPageContext';
import { usePageFiltersContext } from '@/finsight/application/PageFilters/PageFiltersContext';
import keyResolver from '@/Framework/dataHelpers/keyResolver';
import CheckUserPlanAccess from '@/finsight/ui/common/components/CheckUserPlanAccess';
import { useUserPlanContext } from '@/finsight/application/User/UserPlan/UserPlanContext';
import { DealTooltipProvider } from '@/finsight/application/Deal/DealTooltipContext';
import { UserPlanType } from '@/users/domain/UserPlan';
import TogglingFilters from '@/finsight/ui/common/components/TogglingFilters';

import styles from './dealsList.scss';

interface IDealsListProps {
  expandAll?: boolean,
  expandCount?: number,
  title?: string,
  titleToolbar?: any,
  showExpandCollapse?: boolean,
  showDealDetailsByDefault?: boolean,
  innerRef?: (el: HTMLElement) => void,
  profileType?: ProfileTypes,
  containerClassName?: string,
  showDealStatusFilters?: boolean,
}

const getDealIds = memoize((deals) => deals.map((deal) => deal.id) || [], keyResolver);

const DealsList = ({
  expandAll = false,
  expandCount = 0,
  title = 'Recent New Issue',
  titleToolbar = null,
  showExpandCollapse = true,
  showDealDetailsByDefault,
  innerRef = noop,
  profileType,
  containerClassName = '',
  showDealStatusFilters = true,
}: IDealsListProps) => {
  const {
    totalCount,
    collection,
    isFetching,
    subscribeButtonText,
    subscribeSuccessMessage,
    subscriptions,
    filterStatusSet,
    applyDealStatus,
  } = useDealContext();
  const { subscriptionRequiredPlan } = subscriptions || {};
  const dealIds = getDealIds(collection);
  const {
    isStarter,
    isUnauthorized,
  } = useUserPlanContext();
  const getInitialExpandedDeals = () => {
    return expandAll ? dealIds : dealIds.slice(0, expandCount);
  };
  const [expandedDeals, setExpandedDeals] = useState<string[]>(getInitialExpandedDeals());
  const [allExpanded, setAllExpanded] = useState<boolean>(false);
  const [firstFetchIsDone, setFirstFetchIsDone] = useState<boolean>(true);
  const {
    includedProductIds,
    includedRegionIds,
  } = usePageFiltersContext();
  const [productId] = includedProductIds;
  const [regionId] = includedRegionIds;

  const isAllStatusesSelected = filterStatusSet
    .dealStatus
    .items
    .every((item) => item.isActive);
  const hasMoreDeals = (totalCount > collection.length);
  const dealIdsLength = dealIds.length;
  const hasDeals = dealIdsLength !== 0;
  const noData = !hasDeals && !isFetching;
  const isShowExpandCollapse = showExpandCollapse && dealIdsLength && firstFetchIsDone;

  const listWrapperClass = cn(styles.listWrapper, {
    [styles.noDataList]: noData,
    [styles.noDataNoTitle]: noData && !title,
  });

  useEffect(() => {
    if (isFetching && !firstFetchIsDone) {
      setFirstFetchIsDone(true);
    }
  }, [isFetching]);

  useEffect(() => {
    // switch expand/collapse icons upon some of this condition occurrence
    if (isStarter || isUnauthorized) {
      return;
    }
    if (expandedDeals.length === dealIds.length && !allExpanded) {
      setAllExpanded(true);
    } else if (expandedDeals.length < dealIds.length && allExpanded) {
      setAllExpanded(false);
    }
  }, [expandedDeals, dealIds, allExpanded]);

  const showAll = () => {
    setExpandedDeals(dealIds);
  };

  const closeAll = () => {
    setExpandedDeals([]);
  };

  const onDealToggle = (activeKeys: [string]) => {
    setExpandedDeals(activeKeys);
  };

  const onDealHeaderClick = (dealId: string) => {
    setExpandedDeals((prevExpandedDeals) => xor(prevExpandedDeals, [dealId]));
  };

  const renderExpandCollapseIcon = () => (
    allExpanded
      ? <CollapseAllButton onClick={ closeAll } />
      : <ExpandAllButton onClick={ showAll } />
  );

  const renderDeals = useMemo(() => hasDeals && dealIds.map((dealId, index) => (
    <DealRow
      key={ dealId }
      dealId={ dealId }
      index={ index }
      isExpanded={ expandedDeals.includes(dealId) || !!expandedDeals.length }
      onDealHeaderClick={ onDealHeaderClick }
      showDealDetailsByDefault={ showDealDetailsByDefault }
    />
  )), [expandedDeals, hasDeals, dealIds, showDealDetailsByDefault]);

  return (
    <div
      ref={ innerRef }
      className={ cn(styles.container, containerClassName) }
    >
      { (title.length > 0 || (!firstFetchIsDone && isFetching)) && (
        <div className={ styles.header }>
          <div className={ styles.headerInner }>
            { title && <h3 className={ styles.title }>{ title }</h3> }
            { !!titleToolbar && titleToolbar }
          </div>
          <div className={ styles.headerActions }>
            <SubscribeButton
              label={ subscribeButtonText }
              successMessage={ subscribeSuccessMessage }
              isSubscribeAvailable={ !!(regionId && productId && profileType === ProfileTypes.OVERVIEW) }
              requiredPlan={ subscriptionRequiredPlan }
            />
            { showDealStatusFilters && (
              <div className={ styles.dealStatusFiltersWrapper }>
                <TogglingFilters
                  isDisabled={ isFetching || (noData && isAllStatusesSelected) }
                  applyFilter={ applyDealStatus }
                  filterSet={ filterStatusSet }
                  isTooltipEnabled={ false }
                  activeClassName={ styles.dealStatusFiltersActive }
                />
              </div>
            ) }
            { !!isShowExpandCollapse && renderExpandCollapseIcon() }
          </div>
        </div>
      ) }

      <div className={ listWrapperClass }>
        { noData && (
          <h3 className={ styles.noItems }>There are currently no deal cards</h3>
        ) }

        <CheckUserPlanAccess
          requiredUserPlan={ UserPlanType.PREMIUM }
          fallback={ (
            <PanelGroup
              onChange={ onDealToggle }
              activeKey={ expandedDeals }
              prefixCls="dealCard"
              className={ cn({ [styles.expandedCard]: expandedDeals.length }) }
              dataTest="dealsListPanelGroup"
            >
              { renderDeals }
            </PanelGroup>
          ) }
        >
          <PanelGroup
            onChange={ onDealToggle }
            activeKey={ expandedDeals }
            prefixCls="dealCard"
            className={ cn({ [styles.expandedCard]: expandedDeals.length }) }
            dataTest="dealsListPanelGroup"
          >
            { renderDeals }
          </PanelGroup>
        </CheckUserPlanAccess>
      </div>

      { firstFetchIsDone && hasDeals && hasMoreDeals && (
        <LoadMoreButton />
      ) }
    </div>
  );
};

export default (props: Partial<IDealsListProps>) => {
  const { deals } = useParentPageContext();

  return (
    <DealContextProvider deals={ deals }>
      <DealTooltipProvider>
        <CheckUserPlanAccess
          requiredUserPlan={ UserPlanType.PREMIUM }
          fallback={ (
            <DealsList key={ UserPlanType.UNAUTHORIZED } expandAll={ false } { ...props } />
          ) }
        >
          <DealsList key={ UserPlanType.PREMIUM } { ...props } />
        </CheckUserPlanAccess>
      </DealTooltipProvider>
    </DealContextProvider>
  );
};
